import theme from "@shared/utils/themeColors";
import React, { useEffect, useRef, useState } from "react";
import Transition from "../../../utils/Transition";
import { useNotificationsContext } from "../../context/NotificationsContext";
import { NotificationItem } from "./NotificationItem";

interface NotificationsDropdownProps {
  align?: "left" | "right";
}

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
  align = "right",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const dropdown = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLUListElement>(null);

  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    loadMore,
    isLoadingMore,
    markAllAsRead,
    lastNotification,
  } = useNotificationsContext();

  // Handle new notifications
  /* useEffect(() => {
    if (lastNotification.current) {
      const notification = lastNotification.current;
      console.log(notification);
      toast.custom((t) => <NotificationToast notification={notification} />, {
        id: notification.id,
        duration: 8000,
        position: "top-right",
      });
      lastNotification.current = null;
    }
  }, [lastNotification]); */

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target as Node) ||
        trigger.current?.contains(target as Node)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  const handleDropdownOpen = () => {
    setDropdownOpen(!dropdownOpen);
    if (!dropdownOpen && unreadCount > 0) {
      markAllAsRead();
    }
  };

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className={`w-8 h-8 flex items-center justify-center hover:bg-gray-100 lg:hover:bg-gray-200 dark:hover:bg-gray-700/50 dark:lg:hover:bg-gray-800 rounded-full ${
          dropdownOpen && "bg-gray-200 dark:bg-gray-800"
        }`}
        aria-haspopup="true"
        onClick={handleDropdownOpen}
        aria-expanded={dropdownOpen}
      >
        <span className="sr-only">Notifications</span>
        <svg
          className={`fill-current ${theme.text.muted}`}
          width={16}
          height={16}
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M7 0a7 7 0 0 0-7 7c0 1.202.308 2.33.84 3.316l-.789 2.368a1 1 0 0 0 1.265 1.265l2.595-.865a1 1 0 0 0-.632-1.898l-.698.233.3-.9a1 1 0 0 0-.104-.85A4.97 4.97 0 0 1 2 7a5 5 0 0 1 5-5 4.99 4.99 0 0 1 4.093 2.135 1 1 0 1 0 1.638-1.148A6.99 6.99 0 0 0 7 0Z" />
          <path d="M11 6a5 5 0 0 0 0 10c.807 0 1.567-.194 2.24-.533l1.444.482a1 1 0 0 0 1.265-1.265l-.482-1.444A4.962 4.962 0 0 0 16 11a5 5 0 0 0-5-5Zm-3 5a3 3 0 0 1 6 0c0 .588-.171 1.134-.466 1.6a1 1 0 0 0-.115.82 1 1 0 0 0-.82.114A2.973 2.973 0 0 1 11 14a3 3 0 0 1-3-3Z" />
        </svg>
        {unreadCount > 0 && (
          <div
            className={`absolute top-0 right-0 w-2.5 h-2.5 ${theme.bg.error} border-2 border-gray-100 dark:border-gray-900 rounded-full`}
          ></div>
        )}
      </button>

      <Transition
        className={`origin-top-right z-10 absolute top-full -mr-48 sm:mr-0 min-w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1 ${
          align === "right" ? "right-0" : "left-0"
        }`}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        appear={undefined}
      >
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div
            className={`text-xs font-semibold ${theme.text.inactive} uppercase pt-1.5 pb-2 px-4`}
          >
            Notifications
          </div>
          <ul ref={notificationsRef} className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <li className="text-center py-4">
                <div className="animate-pulse">
                  <div
                    className={`h-4 ${theme.bg.tertiary} rounded w-3/4 mx-auto mb-2`}
                  ></div>
                  <div
                    className={`h-4 ${theme.bg.tertiary} rounded w-1/2 mx-auto`}
                  ></div>
                </div>
              </li>
            ) : notifications.length > 0 ? (
              <>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClose={() => setDropdownOpen(false)}
                  />
                ))}
                {hasMore && (
                  <li className="text-center py-2">
                    <button
                      onClick={() => loadMore()}
                      disabled={isLoadingMore}
                      className={`text-sm ${theme.text.info} hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300`}
                    >
                      {isLoadingMore ? "Loading..." : "Load more"}
                    </button>
                  </li>
                )}
              </>
            ) : (
              <li className={`text-center py-4 ${theme.text.muted}`}>
                No notifications
              </li>
            )}
          </ul>
        </div>
      </Transition>
    </div>
  );
};
